/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import { GetOptionLabelParameters } from 'react-crane'

import FormLabel from '../FormLabel'
import Dropdown, { DropdownProps, DropdownMode } from '../Dropdown'

const autoContainerCss = css`
  position: relative;
  padding-bottom: 5px;
`

type AutocompleteOption =
  | {
      [key: string]: string
    }
  | undefined

export type AutocompleteProps = {
  /** Id of control */
  id: string
  /** If the input should be cleared when blurred */
  clearInputOnBlur?: boolean
  /** Label for field */
  label: string
  /** Label key of an option */
  labelKey?: string
  /** Show a static option at the bottom of the results */
  showStaticOption?: boolean
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
  /** Value key of an option */
  valueKey?: string
} & DropdownProps

const defaultProps: Pick<
  AutocompleteProps,
  'clearInputOnBlur' | 'labelKey' | 'showStaticOption' | 'srOnlyLabel' | 'valueKey'
> = {
  clearInputOnBlur: true,
  labelKey: 'label',
  showStaticOption: false,
  srOnlyLabel: false,
  valueKey: 'value'
}

/** Note: this is duplicated in scheduler monolith. */
const Autocomplete = ({
  clearInputOnBlur,
  id,
  inputValue,
  label,
  labelKey,
  placeholder,
  showStaticOption,
  srOnlyLabel,
  value,
  valueKey,
  ...props
}: AutocompleteProps) => {
  const [focused, setFocused] = useState(false)

  const vKey = valueKey || ''
  const lKey = labelKey || ''
  const addItemOption = {
    [vKey]: 'ADD_ITEM_OPTION',
    [lKey]: 'Add your own item'
  }

  const onFocus = () => setFocused(true)
  const onBlur = () => setFocused(false)
  const getOptionLabel = (parameters: GetOptionLabelParameters) => {
    const autoOpt = parameters.option as AutocompleteOption

    if (!autoOpt) {
      return ''
    }

    return showStaticOption && autoOpt[vKey] === addItemOption[vKey]
      ? `Add "${inputValue}"`
      : autoOpt[lKey]
  }

  return (
    <div css={autoContainerCss}>
      <FormLabel
        associatedId={id}
        focused={focused}
        shrink={!!inputValue || focused || !!value}
        srOnly={srOnlyLabel || !!(!focused && placeholder)}
      >
        {label}
      </FormLabel>
      <Dropdown
        {...props}
        id={`${id}-dropdown`}
        arrowComponent={() => null}
        clearInputOnBlur={clearInputOnBlur}
        clearInputOnSelect
        getOptionLabel={getOptionLabel}
        inputId={id}
        inputValue={inputValue}
        isSearchable
        labelKey={labelKey}
        mode={DropdownMode.Simple}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        showInput
        srOnlyLabel={srOnlyLabel}
        staticOption={showStaticOption && inputValue ? addItemOption : undefined}
        value={value}
        valueKey={valueKey}
      />
    </div>
  )
}

Autocomplete.defaultProps = defaultProps

export default Autocomplete
